/** The picture the five form screens were missing.
 *
 * Eighteen of this build's twenty-six scenes are paintings with chrome over
 * them. Five - campaign, journal, load game, settings and the hall of fame -
 * were chrome with nothing behind it, and an art director reading the set blind
 * picked exactly those five out as "a different product". They do not need a
 * new asset class to stop being that: the library already holds twelve 1280x720
 * town paintings and 668 map-object frames, and what was missing was a way to
 * put one behind a panel that is not the shape of the painting.
 *
 * Two pieces, and each is here because it is used by more than one of the five:
 *
 *  - `PaintedPlate` crops a painting to COVER an arbitrary box. `Icon` and
 *    `ScaledSprite` both CONTAIN - they fit the whole frame inside the box and
 *    leave bars - which is right for a socket and wrong for a ground: a 16:9
 *    painting contained in a 3:4 well is the two black bars the new game
 *    screen's faction picker was fixed for, upside down.
 *  - `objectKeyFor` turns the name a screen already prints - "Seer Hut" - into
 *    the map-object frame that object is drawn with on the map, by trying the
 *    prefixes the pack actually uses and taking the first the atlas has. A
 *    lookup table of forty names would be a second place the naming lives.
 *
 * Both keep the build's one rule about sample grids: the ratio is a whole
 * multiple or the reciprocal of a whole divisor, never anything between, and
 * the offset is rounded to a whole pixel. See `theme/atlas.ts` for why.
 */

import { useMemo } from "react";
import type { CSSProperties, ReactNode } from "react";

import { useAtlas } from "../hooks/useAtlas.js";
import type { Atlas } from "../theme/atlas.js";
import { useMeasure } from "./primitives.js";

// ------------------------------------------------------------------ cover

/** The whole ratio at which `srcW x srcH` covers `boxW x boxH`.
 *
 * Positive is a magnification, `1/n` a downscale by exactly `n`. Covering
 * rather than containing is the point: the smallest whole ratio that leaves no
 * gap on either axis. */
export function coverRatio(
  srcW: number, srcH: number, boxW: number, boxH: number,
): number {
  const w = Math.max(1, srcW), h = Math.max(1, srcH);
  const bw = Math.max(1, Math.floor(boxW)), bh = Math.max(1, Math.floor(boxH));
  const div = Math.floor(Math.min(w / bw, h / bh));
  if (div >= 2) return 1 / div;
  if (w >= bw && h >= bh) return 1;
  return Math.max(1, Math.ceil(Math.max(bw / w, bh / h)));
}

/** CSS that fills `boxW x boxH` with the middle of one atlas frame.
 *
 * `fx`/`fy` are the point of the painting held at the centre of the box, 0 to
 * 1. The town paintings put their sky in the top third and their street at the
 * bottom, so a band cut out of one wants `fy` near 0.5 and a tall well wants it
 * lower; nothing here guesses, the caller says. */
export function coverStyle(
  atlas: Atlas, key: string, boxW: number, boxH: number, fx = 0.5, fy = 0.5,
): CSSProperties {
  const frame = atlas.frame(key);
  if (frame === null || boxW <= 0 || boxH <= 0) return {};
  const sheet = atlas.manifest.sheets.find((s) => s.id === frame.sheet);
  if (sheet === undefined) return {};
  const k = coverRatio(frame.w, frame.h, boxW, boxH);
  // The frame's own origin on the scaled sheet, then back off by however much
  // of the scaled frame does not fit, weighted by the focus point.
  const overX = frame.w * k - boxW;
  const overY = frame.h * k - boxH;
  return {
    backgroundImage: `url(${atlas.sheetUrl(sheet.id)})`,
    backgroundSize: `${String(sheet.w * k)}px ${String(sheet.h * k)}px`,
    backgroundPosition:
      `${String(Math.round(-frame.x * k - overX * fx))}px `
      + `${String(Math.round(-frame.y * k - overY * fy))}px`,
    backgroundRepeat: "no-repeat",
  };
}

export interface PaintedPlateProps {
  /** An atlas key, normally `town.<faction>.background`. */
  readonly atlasKey: string;
  /** 0-1, the point of the painting held at the centre of the plate. */
  readonly focusX?: number;
  readonly focusY?: number;
  /** Extra classes on the plate itself; `hm-plate` is always present. */
  readonly className?: string;
  /** Drawn over the painting, inside the frame. */
  readonly children?: ReactNode;
  readonly style?: CSSProperties;
}

/**
 * A painting behind whatever is put on it, in a struck frame.
 *
 * The plate measures itself rather than taking a size, because the whole point
 * is that it takes the room a screen has left over - and how much that is, is a
 * layout question that only the browser can answer. Before the first measure
 * the painting is not drawn at all: a wrong ratio for one frame is a visible
 * shimmer, and an empty frame for one frame is not.
 */
export function PaintedPlate(props: PaintedPlateProps): JSX.Element {
  const atlas = useAtlas();
  const [ref, box] = useMeasure<HTMLDivElement>();
  const art = useMemo<CSSProperties>(
    () => coverStyle(atlas, props.atlasKey, box.w, box.h, props.focusX ?? 0.5, props.focusY ?? 0.5),
    [atlas, props.atlasKey, box.w, box.h, props.focusX, props.focusY],
  );
  return (
    <div
      ref={ref}
      className={`hm-plate ${props.className ?? ""}`}
      style={props.style}
    >
      <span className="hm-plate__art" style={art} aria-hidden="true" />
      <span className="hm-plate__grade" aria-hidden="true" />
      {props.children === undefined ? null : (
        <div className="hm-plate__over">{props.children}</div>
      )}
    </div>
  );
}

// ------------------------------------------------------------- map objects

/** The prefixes `tools/pack-assets.ts` files map objects under, in the order a
 *  name is likelier to be found: buildings, then travel, then dwellings, then
 *  the expansion's own, then scenery and pickups. */
const OBJECT_PREFIXES: readonly string[] = Object.freeze([
  "obj.bld_", "obj.tp_", "obj.dwl_", "obj.expansion-", "obj.obs_", "obj.pu_", "obj.mine_", "obj.",
]);

/** `"Oracle's Hut"` -> `seer_hut`. Apostrophes and hyphens go, everything else
 *  that is not a letter or a digit becomes the underscore the pack uses. */
export function objectSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/['’]/g, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

/**
 * The map-object frame an object printed by name is drawn with, or null.
 *
 * Null rather than a fallback sprite on purpose: a journal row wearing the
 * wrong building is a lie about where the player has to walk, and an empty
 * socket is not.
 */
export function objectKeyFor(atlas: Atlas, name: string): string | null {
  const slug = objectSlug(name);
  if (slug === "") return null;
  for (const p of OBJECT_PREFIXES) {
    const key = `${p}${slug}`;
    if (atlas.has(key)) return key;
  }
  return null;
}

/** The twelve town crests, in the order the faction list is written in
 *  everywhere else in this build. Every one is 225px wide in the pack, which is
 *  what lets them be set as a frieze without a per-faction size. */
export const FACTIONS: readonly string[] = Object.freeze([
  "sunspire", "greenmantle", "skyreach", "emberpit", "gravewatch", "deepvault",
  "ironhorde", "fenmire", "prismgate", "saltreach", "cogworks", "frostmark",
]);

/** A faction's keep, as the town screen draws it. */
export const crestKey = (faction: string): string => `town.${faction}.castle`;

// ------------------------------------------------------------------ styles

/** Mounted by each screen that uses a plate, appended to that screen's own
 *  sheet rather than mounted separately - one more `<style>` element per screen
 *  is one more thing to get the order of. No backticks below. */
export const FORM_ART_CSS = `
/* The plate: a painting, a grade over it so type can be read off it, and a
   struck frame. The frame is the same six-stop contour every socket in this
   build carries, at the weight a picture rather than an icon takes. */
.hm-plate {
  position: relative;
  overflow: hidden;
  min-width: 0;
  min-height: 0;
  background-color: #100b06;
  box-shadow: 0 10px 30px rgba(0,0,0,0.62);
}
.hm-plate__art {
  position: absolute;
  inset: 0;
  image-rendering: pixelated;
  image-rendering: crisp-edges;
}
/* THE FRAME IS A LAYER, NOT A BOX-SHADOW ON THE PLATE.
   An inset box-shadow paints over the element's own background and *under* its
   children, and the painting is a child at inset:0 - so for four screens the
   declared 1px black / 3px gold / 1px black contour existed in the sheet and
   rendered zero gold pixels on a 1892x1007 perimeter. A pseudo-element over the
   art is the only place a frame can go on a plate whose content fills it.

   Four one-sided lips rather than one ring, because the kit's light comes from
   the upper left (see theme/relief.ts) and a ring has no direction. 'inset 0 5px
   0 -1px' is the idiom: the shadow rect is grown 1px and pushed down 5px, so
   what is left unpainted is a 4px band on ONE edge that runs corner to corner.
   The 1px outer hairline is listed first and covers the outer pixel of all four,
   which leaves the declared 3px of gilt. The bottom lip is the darkest gold on
   the plate and not black: a rail that stops being gold at the bottom edge is
   the three-sided frame this kit keeps drawing by accident. */
.hm-plate::after {
  content: "";
  position: absolute;
  inset: 0;
  z-index: 2;
  pointer-events: none;
  box-shadow:
    inset 0 0 0 1px rgba(0,0,0,0.94),
    inset 0 5px 0 -1px #f2dba4,
    inset 5px 0 0 -1px #d5b060,
    inset -5px 0 0 -1px #9c7526,
    inset 0 -5px 0 -1px #856021,
    inset 0 0 0 5px rgba(0,0,0,0.85);
}
/* Paintings made to be a town screen are lit for a town screen: put one behind
   a page of 12px type and the type is unreadable over the bright half.

   THE GRADE IS STEPPED, AND EVERY STEP IS BELOW THE EYE'S CONTOUR THRESHOLD.
   Two smooth alpha ramps over quantised art give every source colour a new
   value at every row it appears in - a 60x60 patch of sky ran 579 distinct
   colours, which is what made the picture read as a photograph behind a brown
   scrim. Hard stops hold the palette: a patch that crosses no boundary comes
   out with exactly the colours the painting had.

   But a step is a drawn line if it is big enough to see, and the first cut of
   this took the top band down 0.18 alpha in one go: measured as row-mean luma,
   +35 at y=98 on Settings and +28 at y=374 on Campaign, which is a ruled line
   1860px wide straight through a mountain. NO STEP HERE EXCEEDS 0.04 ALPHA. On
   the brightest thing a painting holds - sky at about 190 - that is under eight
   levels, which is the band a step stops being a contour and becomes shading.
   Sixteen stops vertically and eleven radially rather than six of each; the
   count is what buys the step size, and there is no cheaper way to buy it.

   The 2px checker that was here is gone and must not come back. It was a
   halftone locked to SCREEN coordinates, not to the art, so it crossed every
   object edge and did not move with the picture: measured as the spread of the
   four (y%4, x%4) parity means, 1.66 before it and 8.26 after. It bought a
   colour count the metric liked and cost a window screen over every painting. */
.hm-plate__grade {
  position: absolute;
  inset: 0;
  background-image:
    radial-gradient(122% 96% at 50% 40%,
      rgba(9,6,3,0) 0 40%, rgba(9,6,3,0.03) 40% 48%, rgba(9,6,3,0.06) 48% 55%,
      rgba(9,6,3,0.10) 55% 62%, rgba(9,6,3,0.14) 62% 69%, rgba(9,6,3,0.18) 69% 75%,
      rgba(8,5,3,0.22) 75% 81%, rgba(8,5,3,0.26) 81% 86%, rgba(8,5,3,0.30) 86% 91%,
      rgba(8,5,3,0.34) 91% 96%, rgba(6,4,2,0.38) 96% 100%),
    linear-gradient(180deg,
      rgba(12,8,4,0.26) 0 3%, rgba(12,8,4,0.23) 3% 6%, rgba(12,8,4,0.20) 6% 9%,
      rgba(12,8,4,0.17) 9% 13%, rgba(11,7,4,0.14) 13% 18%, rgba(11,7,4,0.11) 18% 24%,
      rgba(10,7,3,0.09) 24% 34%, rgba(10,7,3,0.11) 34% 44%, rgba(9,6,3,0.14) 44% 54%,
      rgba(9,6,3,0.17) 54% 63%, rgba(9,6,3,0.20) 63% 71%, rgba(8,5,3,0.24) 71% 78%,
      rgba(8,5,3,0.28) 78% 85%, rgba(8,5,3,0.32) 85% 91%, rgba(8,5,3,0.36) 91% 96%,
      rgba(8,5,3,0.40) 96% 100%);
  background-repeat: no-repeat, no-repeat;
}
/* Deeper, for a plate that carries a page of body copy rather than a heading.
   Same rule: the floor is higher, the steps are the same size. */
.hm-plate--deep .hm-plate__grade {
  background-image:
    radial-gradient(126% 104% at 50% 40%,
      rgba(10,7,4,0.24) 0 34%, rgba(10,7,4,0.28) 34% 42%, rgba(10,7,4,0.32) 42% 50%,
      rgba(9,6,3,0.36) 50% 57%, rgba(9,6,3,0.40) 57% 64%, rgba(9,6,3,0.44) 64% 71%,
      rgba(8,5,3,0.48) 71% 77%, rgba(8,5,3,0.52) 77% 83%, rgba(8,5,3,0.56) 83% 89%,
      rgba(7,4,2,0.60) 89% 95%, rgba(6,4,2,0.64) 95% 100%),
    linear-gradient(180deg,
      rgba(12,8,4,0.44) 0 4%, rgba(12,8,4,0.41) 4% 8%, rgba(12,8,4,0.38) 8% 13%,
      rgba(12,8,4,0.35) 13% 19%, rgba(11,7,4,0.32) 19% 26%, rgba(11,7,4,0.30) 26% 34%,
      rgba(10,7,3,0.32) 34% 42%, rgba(10,7,3,0.35) 42% 50%, rgba(9,6,3,0.38) 50% 58%,
      rgba(9,6,3,0.41) 58% 66%, rgba(9,6,3,0.44) 66% 73%, rgba(8,5,3,0.47) 73% 80%,
      rgba(8,5,3,0.50) 80% 86%, rgba(8,5,3,0.53) 86% 92%, rgba(8,5,3,0.56) 92% 97%,
      rgba(8,5,3,0.59) 97% 100%);
  background-repeat: no-repeat, no-repeat;
}
/* THE CORNERS ARE THE ONE PLACE THIS KIT DRAWS SOMETHING.
   The frame above is six stops of box-shadow, which is a border however well it
   is lit, and six screens of borders is why the set read as admin panels in a
   brown skin. The reference closes every page with a carved gilt block at each
   corner; \`polish.ts\` owns that block and hands it over as four tokens, one per
   rotation. Above the frame rather than under it - the strap is what the two
   rails run into, so it has to be the thing that covers their junction. */
.hm-plate::before {
  content: "";
  position: absolute;
  inset: 0;
  z-index: 3;
  pointer-events: none;
  background-image:
    var(--hm-corner-tl, none), var(--hm-corner-tr, none),
    var(--hm-corner-br, none), var(--hm-corner-bl, none);
  background-position: 0 0, 100% 0, 100% 100%, 0 100%;
  background-size: 30px 30px;
  background-repeat: no-repeat;
}
.hm-plate__over {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  height: 100%;
  padding: 18px 22px;
}
/* A cartouche: the one thing that makes type on a painting read as engraved
   rather than pasted. Gilt hairline, black ground, and it is only ever as wide
   as the words in it. */
.hm-cart {
  position: relative;
  text-align: center;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 7px;
  max-width: min(56ch, 92%);
  padding: 12px 22px 14px;
  background-color: rgba(14,10,5,0.82);
  /* The kit's one vellum, from polish.ts. What was here was a six-pixel comb
     at 97deg, byte-copied into five other screens; the token is the fix for all
     six and this is the copy this file owned. */
  background-image:
    var(--hm-vellum, none),
    linear-gradient(178deg, rgba(96,70,38,0.55) 0%, rgba(40,28,14,0.65) 100%);
  /* THE TOKEN CHANGES THE LAYER COUNT, SO THE SIZE LIST HAS TO BE RESTATED.
     \`background-size\` and \`background-repeat\` are lists that cycle when they
     are shorter than \`background-image\`, and \`reliefDecls\` emits three of each
     for a wood plate. A call site that swaps a one-layer comb for the token's
     three and leaves the sizes alone hands the opaque base gradient the wood
     fibre's \`331px 331px\`, and a base gradient tiled at 331px is a sawtooth:
     measured on the Journal's chronicle card, a 35-luma butt join at y=384,
     715 and 1046. Every rule in this kit that names the vellum restates both. */
  background-size: auto;
  background-repeat: repeat;
  /* The same four one-sided lips the plate frame is built from, at the weight a
     text box takes. A single 'inset 0 0 0 2px' ring cannot be used here: the
     black hairline is listed first, so it paints over the outer pixel of the
     ring and what survived was one pixel of gilt at 0.34 alpha - a cartouche
     that read as a translucent panel laid on the painting rather than as a
     plate struck into it. Directional, and the bottom lip is dark gold rather
     than black, for the reason the plate's is. */
  box-shadow:
    inset 0 0 0 1px rgba(0,0,0,0.92),
    inset 0 3px 0 -1px rgba(238,208,150,0.82),
    inset 3px 0 0 -1px rgba(206,168,92,0.70),
    inset -3px 0 0 -1px rgba(150,113,44,0.74),
    inset 0 -3px 0 -1px rgba(126,94,36,0.80),
    0 8px 22px rgba(0,0,0,0.62);
}
/* A cartouche is too small for the corner strap the plate carries, so it gets
   the ornament the strap reduces to: the gem alone, set into the head and foot
   rails at their centre. It is the same token, so a cartouche and a page are
   ornamented out of one drawing rather than two. */
.hm-cart::before {
  content: "";
  position: absolute;
  inset: 0;
  pointer-events: none;
  background-image: var(--hm-gem, none), var(--hm-gem, none);
  background-position: 50% -1px, 50% calc(100% + 1px);
  background-size: 13px 13px;
  background-repeat: no-repeat;
}
.hm-cart__lead {
  font-family: var(--hm-font-display, 'Palatino Linotype', 'Book Antiqua', Palatino, Georgia, serif);
  font-size: max(clamp(15px, 1.25vw, 21px), var(--hm-type-floor));
  letter-spacing: 0.04em;
  color: #f2dcac;
  text-shadow: 0 2px 0 #1c1206, 0 3px 10px rgba(0,0,0,0.9);
  text-wrap: balance;
}
/* Balanced rather than a narrower measure: the cartouche is set in whatever
   column the screen has left, so a fixed 'ch' cap cannot know where the ragged
   edge will fall. Load Game's aside was ending on "one of them." alone, which on
   a plate this small is the bottom rule of the frame carrying two words.
   'balance', not 'pretty' - measured in the shipping Chromium on this exact
   string at this exact measure, 'pretty' laid out identically to no value at
   all and 'balance' evened the three lines. */
.hm-cart__note {
  max-width: 46ch;
  text-wrap: balance;
  margin: 0;
  font-family: var(--hm-font-display, 'Palatino Linotype', 'Book Antiqua', Palatino, Georgia, serif);
  font-size: max(12.5px, var(--hm-type-floor));
  line-height: 1.55;
  color: #d6bf94;
  text-shadow: 0 1px 0 rgba(0,0,0,0.9);
}
/* A socket for a map object, cut into whatever it is set on. */
.hm-objsock {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  background-image:
    radial-gradient(75% 60% at 50% 18%, rgba(255,226,176,0.12) 0%, rgba(0,0,0,0) 68%),
    linear-gradient(180deg, rgba(0,0,0,0.42) 0%, rgba(0,0,0,0.14) 55%, rgba(0,0,0,0.40) 100%);
  box-shadow:
    inset 0 0 0 1px rgba(0,0,0,0.85),
    inset 0 1px 0 rgba(255,232,186,0.14),
    inset 0 -1px 0 rgba(0,0,0,0.5);
}
`;
